import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { ScreenHeader, Section, TermSheet } from '../../components/common';
import { LessonFigure } from '../../components/diagrams';
import { findTerm, type GlossaryTerm } from '../glossary/terms';
import { useApp } from '../../state/AppState';
import { findLesson } from './curriculum';
import { lessonAfter, lessonBefore } from './LearnContents';
import { markLessonDone, readLearnDone } from './LearnScreen';
import { LinkedParagraph } from './linkTerms';

/**
 * One lesson: the body paragraphs, every glossary term used as a tappable
 * chip, and the checkpoint as a one-question quiz that explains WHY on both
 * right and wrong answers.
 *
 * A page of a book, not a step of a course. No lesson assumes the reader has
 * seen the one before it, so all three exits are equal citizens: back to the
 * contents page, back one lesson, or on to the next. "Next lesson" stays the
 * primary button because forward is still the commonest thing to want — not
 * because it is the only thing allowed.
 *
 * Completion rules (shared with LearnScreen's helpers): answering the
 * checkpoint finishes the lesson — either way, because the explanation is the
 * teaching, not the score. A checkpoint-less lesson finishes when the reader
 * leaves it, in WHICHEVER direction: browsing backwards through a reference
 * book is reading, so it counts the same as walking forwards. The next-lesson
 * CTA is never disabled; an unanswered checkpoint is named next to it instead
 * (SocialOptInScreen's contract).
 */
export function LessonView() {
  const { t } = useI18n();
  const app = useApp();
  const navigate = useNavigate();
  const { lessonId } = useParams<{ lessonId: string }>();
  const hit = findLesson(lessonId);

  const [done, setDone] = useState<ReadonlySet<string>>(new Set<string>());
  const [picked, setPicked] = useState<number | null>(null);
  const [openTerm, setOpenTerm] = useState<string | null>(null);

  // Lesson→lesson navigation keeps this component mounted: reset the quiz,
  // reload progress, and put the reader back at the top of the new lesson.
  useEffect(() => {
    setPicked(null);
    setOpenTerm(null);
    window.scrollTo?.(0, 0);
    let alive = true;
    readLearnDone().then((set) => {
      if (alive) setDone(set);
    });
    return () => {
      alive = false;
    };
  }, [lessonId]);

  // An unknown id lands on the module list, not an error screen.
  if (!hit) return <Navigate to="/learn" replace />;

  const { module, lesson } = hit;
  const checkpoint = lesson.checkpoint;
  const next = lessonAfter(lesson.id);
  const prev = lessonBefore(lesson.id);
  const finished = done.has(lesson.id);

  // A single figure opens the lesson — picture first, prose under it. A
  // multi-figure lesson (the chart-styles tour) puts its gallery AFTER the
  // body instead: three stacked kundlis before a word of text would push the
  // teaching below the fold.
  const figures = !lesson.figure
    ? []
    : Array.isArray(lesson.figure)
      ? lesson.figure
      : [lesson.figure];
  const figuresAbove = figures.length === 1 ? figures : [];
  const figuresBelow = figures.length > 1 ? figures : [];

  // Every id resolves in the current curriculum (the test suite enforces it);
  // the filter just keeps a future bad id from rendering a dead chip.
  const terms = lesson.terms
    .map((id) => findTerm(id))
    .filter((entry): entry is GlossaryTerm => Boolean(entry));

  const answer = (index: number) => {
    if (picked !== null) return; // first answer stands; the why is now visible
    setPicked(index);
    void markLessonDone(lesson.id, app.backend).then(setDone);
  };

  /**
   * Leave for anywhere. Reading a checkpoint-less lesson IS finishing it, so
   * every exit records it — forwards, backwards, or out to the contents. Tying
   * that to the "next" button alone would have meant a reader who browses the
   * book instead of marching through it never got credit for anything.
   */
  const leave = async (to: string) => {
    if (!checkpoint) {
      const set = await markLessonDone(lesson.id, app.backend);
      setDone(set);
    }
    navigate(to);
  };

  return (
    <div
      className="screen"
      data-mode={module.system !== 'shared' ? module.system : undefined}
    >
      <ScreenHeader
        title={lesson.title}
        subtitle={`${module.title} · ${t('learn.minutes', { n: lesson.minutes })}`}
        back
        right={
          // The running header of a book: the contents are always one tap
          // away, whichever screen the reader arrived from.
          <Link
            to="/learn"
            className="tiny accent"
            style={{ flexShrink: 0 }}
            onClick={() => {
              if (!checkpoint) void markLessonDone(lesson.id, app.backend).catch(() => undefined);
            }}
          >
            {t('learn.contents')}
          </Link>
        }
      />

      {figuresAbove.map((id) => (
        <div key={id} className="card" style={{ padding: 12, marginBottom: 14 }}>
          <LessonFigure id={id} />
        </div>
      ))}

      <div className="col">
        {lesson.body.map((paragraph, i) => (
          <p key={i} className="small" style={{ lineHeight: 1.65, margin: 0 }}>
            <LinkedParagraph text={paragraph} entries={terms} />
          </p>
        ))}
      </div>

      {figuresBelow.length > 0 && (
        <div className="col" style={{ marginTop: 14 }}>
          {figuresBelow.map((id) => (
            <div key={id} className="card" style={{ padding: 12 }}>
              <LessonFigure id={id} />
            </div>
          ))}
        </div>
      )}

      {lesson.seeOnKundli && (
        // The founder's bridge: the example diagrams and the reader's own
        // chart are one tap apart.
        <Link
          to="/charts"
          className="small accent center"
          style={{ display: 'block', marginTop: 14 }}
        >
          {t('learn.seeOnKundli')} →
        </Link>
      )}

      {terms.length > 0 && (
        <div className="wrap" style={{ marginTop: 14 }}>
          {terms.map((entry) => (
            <button key={entry.id} className="chip" onClick={() => setOpenTerm(entry.id)}>
              {entry.title}
            </button>
          ))}
        </div>
      )}

      {checkpoint && (
        <Section title={t('learn.checkpoint')}>
          <div className="card col">
            <div className="small" style={{ fontWeight: 600, lineHeight: 1.55 }}>
              {checkpoint.question}
            </div>
            <div className="col">
              {checkpoint.options.map((option, i) => {
                const revealed = picked !== null;
                const isRight = i === checkpoint.answerIndex;
                const isPicked = picked === i;
                return (
                  <button
                    key={i}
                    className={`chip ${revealed && isRight ? 'chip--on' : ''}`}
                    aria-pressed={isPicked}
                    style={{
                      textAlign: 'left',
                      width: '100%',
                      lineHeight: 1.45,
                      opacity: revealed && !isRight && !isPicked ? 0.55 : 1,
                      borderColor: revealed && isPicked && !isRight ? 'var(--danger)' : undefined,
                    }}
                    onClick={() => answer(i)}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
            {picked !== null && (
              <div
                className={`banner small ${picked === checkpoint.answerIndex ? '' : 'banner--info'}`}
                role="status"
                style={
                  picked === checkpoint.answerIndex
                    ? { background: 'var(--ok-soft)', color: 'var(--ok)' }
                    : undefined
                }
              >
                <div style={{ fontWeight: 700 }}>
                  {picked === checkpoint.answerIndex ? t('learn.correct') : t('learn.notQuite')}
                </div>
                <div style={{ marginTop: 4, lineHeight: 1.55 }}>{checkpoint.why}</div>
              </div>
            )}
          </div>
        </Section>
      )}

      <div className="col" style={{ margin: '16px 0', gap: 10 }}>
        {checkpoint && picked === null && !finished && (
          <div className="tiny faint center">{t('learn.checkpointHint')}</div>
        )}
        <div className="row">
          {prev && (
            <button
              className="btn btn--ghost"
              // Shrinks before the primary does on a narrow phone, and never
              // wraps its own label.
              style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              onClick={() => leave(`/learn/${prev.lesson.id}`)}
            >
              <span aria-hidden>←</span> {t('learn.prev')}
            </button>
          )}
          <button
            className="btn btn--primary"
            style={{ flex: 1 }}
            onClick={() => leave(next ? `/learn/${next.lesson.id}` : '/learn')}
          >
            {next ? t('learn.next') : t('learn.done')}
          </button>
        </div>
        {next && (
          // Only when "next" is on screen: without it the primary button
          // already says "back to all modules".
          <button
            className="btn btn--ghost btn--small btn--block"
            onClick={() => leave('/learn')}
          >
            {t('learn.contents')}
          </button>
        )}
      </div>

      {openTerm && <TermSheet term={openTerm} onClose={() => setOpenTerm(null)} />}
    </div>
  );
}
